import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { Tracking } from '@freelancer/tracking';
import { CookieService } from '@laurentgoudet/ngx-cookie';
import * as Rx from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import {
  ProjectExperiments,
  SessionExperiments,
  UserExperiments,
} from './experiments';

export enum ABTestType {
  SESSION_BASED = 1,
  USER_BASED = 2,
  PROJECT_BASED = 3,
}

export interface ABTestApiResponse<T extends keyof Experiments> {
  state: 'in_test';
  test_id: number;
  variation_name: Experiments[T];
}

export type VariationName = string;

export const STORAGE_KEY = 'WEBAPP_ABTEST_CACHE_V1';

type Experiments = SessionExperiments & UserExperiments & ProjectExperiments;

// FIXME(T66231): we should make the A/B test service return a tuple of
// `response | error` instead of throwing exceptions, as handling of these
// later can't be enforced in TypeScript.
//
// For now, make sure you catch any ABTest service failure or bad things will
// happen when the backend is down.
@Injectable()
export class ABTest {
  constructor(
    private auth: Auth,
    private cookies: CookieService,
    private freelancerHttp: FreelancerHttp,
    private tracking: Tracking,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  getSessionExperimentVariation<T extends keyof SessionExperiments>(
    experimentName: T,
  ): Rx.Observable<SessionExperiments[T] | undefined> {
    return this.tracking
      .getSessionId()
      .pipe(
        take(1),
        switchMap(sessionId =>
          this.getVariation(
            ABTestType.SESSION_BASED,
            experimentName,
            sessionId,
          ),
        ),
      );
  }

  // userId is optional, as it'll default to the user's own id
  getUserExperimentVariation<T extends keyof UserExperiments>(
    experimentName: T,
    userId?: number,
  ): Rx.Observable<UserExperiments[T] | undefined> {
    const userIdObservable = userId
      ? Rx.of(userId)
      : this.auth.getUserId().pipe(map(uid => parseInt(uid, 10)));
    return userIdObservable.pipe(
      take(1),
      switchMap(uid =>
        this.getVariation(ABTestType.USER_BASED, experimentName, uid),
      ),
    );
  }

  getProjectExperimentVariation<T extends keyof ProjectExperiments>(
    experimentName: T,
    projectId: number,
  ): Rx.Observable<ProjectExperiments[T] | undefined> {
    return this.getVariation(
      ABTestType.PROJECT_BASED,
      experimentName,
      projectId,
    );
  }

  private getVariation<T extends keyof Experiments>(
    experimentType: ABTestType,
    experimentName: T,
    identifier: string | number,
  ): Rx.Observable<Experiments[T] | undefined> {
    // global test disabling flag, used by automation tools
    if (this.cookies.get('no_abtest') !== undefined) {
      return Rx.of(undefined);
    }
    // test overrides functionality
    const overrides = new URL(window.location.href).searchParams.get(
      'overrides',
    );
    if (overrides) {
      const overridesMap = overrides
        .split(',')
        .reduce<{ [experimentName: string]: Experiments[T] }>(
          (acc, experiment) => {
            const [name, variation] = experiment.split(':');
            acc[name] = <Experiments[T]>variation;
            return acc;
          },
          {},
        );
      if (overridesMap[experimentName]) {
        return Rx.of(overridesMap[experimentName]);
      }
    }
    // try reading the variation from cache if it exists
    const cacheKey = `${STORAGE_KEY}|${experimentType}|${experimentName}|${identifier}`;
    // skip cache when on the server
    if (isPlatformBrowser(this.platformId)) {
      let variationFromStorage: Experiments[T] | undefined;
      try {
        variationFromStorage =
          <Experiments[T]>window.sessionStorage.getItem(cacheKey) || '';
      } catch (e) {
        // ignore the errors, e.g. quota is full or security error
        console.error(e);
      }
      if (variationFromStorage) {
        return Rx.of(variationFromStorage);
      }
    }
    // overwise get the variation for the A/B test server
    return this.freelancerHttp
      .post<ABTestApiResponse<T>>('abtest/0.1/enrollments/', {
        id_type: experimentType,
        experiment_name: experimentName,
        id: identifier,
      })
      .pipe(
        map(response => {
          if (response.status !== 'success') {
            return undefined;
          }
          return response.result.variation_name;
        }),
        tap(variationName => {
          // skip cache when on the server or on error
          if (
            variationName !== undefined &&
            isPlatformBrowser(this.platformId)
          ) {
            try {
              window.sessionStorage.setItem(cacheKey, variationName);
            } catch (e) {
              // ignore the errors, e.g. quota is full or security error
              console.error(e);
            }
          }
        }),
        catchError(() => Rx.of(undefined)),
      );
  }
}
